import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import useEntertainment from '../../hooks/useEntertainment';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function EmergencyPrintMode() {
  const authCtx = useContext(AuthContext);
  const { getEntertainments, setEmergencyPrintMode } = useEntertainment();
  const getEntertainmentsRef = useRef(getEntertainments);

  useEffect(() => {
    getEntertainmentsRef.current = getEntertainments;
  }, [getEntertainments]);

  const [loading, setLoading] = useState(false);
  const [savingById, setSavingById] = useState({});
  const [entertainments, setEntertainments] = useState([]);

  const canAccess = useMemo(
    () => authCtx.checkRight('entertainment:special:emergencyPrintMode'),
    [authCtx]
  );

  useEffect(() => {
    if (!canAccess) return;
    let active = true;

    const load = async () => {
      setLoading(true);
      try {
        const res = await getEntertainmentsRef.current(true);
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        if (!active) return;
        setEntertainments(list);
      } catch (e) {
        toast.error('Fehler beim Laden der Entertainments');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [canAccess]);

  const sorted = useMemo(() => {
    const list = Array.isArray(entertainments) ? [...entertainments] : [];
    list.sort((a, b) => String(a?.longName || '').localeCompare(String(b?.longName || '')));
    return list;
  }, [entertainments]);

  const toggle = async (entertainmentId, nextValue) => {
    if (!entertainmentId) return;
    setSavingById(prev => ({ ...prev, [entertainmentId]: true }));
    setEntertainments(prev =>
      (prev || []).map(e =>
        String(e?._id) === String(entertainmentId) ? { ...e, emergencyPrintMode: nextValue } : e
      )
    );

    try {
      const updated = await setEmergencyPrintMode({
        entertainmentId,
        emergencyPrintMode: nextValue,
      });
      if (updated && updated._id) {
        setEntertainments(prev =>
          (prev || []).map(e => (String(e?._id) === String(updated._id) ? updated : e))
        );
      }
      toast.success('Notfallmodus gespeichert');
    } catch (e) {
      setEntertainments(prev =>
        (prev || []).map(e =>
          String(e?._id) === String(entertainmentId)
            ? { ...e, emergencyPrintMode: !nextValue }
            : e
        )
      );
    } finally {
      setSavingById(prev => ({ ...prev, [entertainmentId]: false }));
    }
  };

  if (!canAccess) {
    return (
      <Box sx={{ ...viewRootTextPrimarySx, p: 3 }}>
        <Alert severity='error'>Du hast keine Berechtigung für diesen Bereich.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: 3 }}>
      <Stack spacing={2}>
        <Typography variant='h5'>Notfallmodus Druck</Typography>
        <Alert severity='warning'>
          Wenn aktiviert, werden neu angelegte Spielerkarten nicht gedruckt, sondern direkt als
          „Verknüpfen fehlgeschlagen“ markiert. Die Karte kann dann manuell beschriftet und später
          über „Karte verknüpfen“ am RFID-Reader zugeordnet werden.
        </Alert>
        <Paper variant='outlined' sx={{ p: 2 }}>
          {loading ? (
            <Box sx={{ ...viewRootTextPrimarySx, display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : sorted.length === 0 ? (
            <Typography color='text.secondary'>Keine Entertainments gefunden.</Typography>
          ) : (
            <Stack divider={<Divider flexItem />} spacing={0}>
              {sorted.map(e => {
                const id = e?._id ? String(e._id) : '';
                const isOn = Boolean(e?.emergencyPrintMode);
                const isSaving = Boolean(savingById[id]);
                return (
                  <Box
                    key={id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1,
                      gap: 2,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {e?.longName || e?.entertainmentName || id}
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {e?.entertainmentName || ''}
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isOn}
                          onChange={(_, checked) => toggle(id, checked)}
                          disabled={isSaving}
                        />
                      }
                      label={isOn ? 'Aktiv' : 'Inaktiv'}
                      sx={{ m: 0 }}
                    />
                  </Box>
                );
              })}
            </Stack>
          )}
        </Paper>
      </Stack>
    </Box>
  );
}

export default EmergencyPrintMode;

